/** @graphql cc **/
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import { gql } from 'graphql-tag';

import type { CustomInclusion } from '@confluence/user-and-group-search/entry-points/UserAndGroupSearchPicker';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';

import type {
	useAccessClassInclusionClassesQuery as useAccessClassInclusionClassesQueryType,
	useAccessClassInclusionClassesQueryVariables,
} from './__types__/useAccessClassInclusionClassesQuery';

const useAccessClassInclusionClassesQuery = gql`
	query useAccessClassInclusionClassesQuery($cloudId: ID!) {
		confluence_userClasses(cloudId: $cloudId) {
			nodes {
				principalId
				displayName
			}
		}
	}
`;

const toLowerWithoutSpaces = (str: string) => str?.toLowerCase().replace(/ /g, '');

export const useAccessClassInclusion = (): CustomInclusion => {
	const { cloudId } = useSessionData();
	const { data, loading, error } = useQuery<
		useAccessClassInclusionClassesQueryType,
		useAccessClassInclusionClassesQueryVariables
	>(useAccessClassInclusionClassesQuery, {
		variables: { cloudId },
		onError: (e) => {
			getMonitoringClient().submitError(e, {
				attribution: Attribution.PERMISSIONS_EXPERIENCE,
			});
			markErrorAsHandled(e);
		},
	});

	const allConfluenceUsers = data?.confluence_userClasses?.nodes?.find(
		(userClass) => userClass?.principalId === 'all-licensed-users',
	);

	const allConfluenceAdmins = data?.confluence_userClasses?.nodes?.find(
		(userClass) => userClass?.principalId === 'all-product-admins',
	);

	if (loading || error) {
		return [];
	}

	return [
		{
			...(allConfluenceUsers?.principalId &&
				allConfluenceUsers?.displayName && {
					[toLowerWithoutSpaces(allConfluenceUsers.displayName)]: {
						id: allConfluenceUsers.principalId,
						name: allConfluenceUsers.displayName,
						type: 'custom',
						avatarUrl: require('./images/atlassian-icon.svg'),
					},
				}),
			...(allConfluenceAdmins?.principalId &&
				allConfluenceAdmins?.displayName && {
					[toLowerWithoutSpaces(allConfluenceAdmins.displayName)]: {
						id: allConfluenceAdmins.principalId,
						name: allConfluenceAdmins.displayName,
						type: 'custom',
						avatarUrl: require('./images/atlassian-icon.svg'),
					},
				}),
		},
	];
};
